import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSwipeable } from "react-swipeable";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import debounce from "lodash/debounce";
import "./Product.css";
import ProductHeaderV2 from "../../components/v2/ProductHeaderV2";
import ProductGallery from "../../components/v2/product/ProductGallery";
import ScoreInfo from "../../components/v2/ScoreInfo";
import ScoreVertical from "../../components/v2/ScoreVertical";
import IndicatorInfo from "../../components/v2/IndicatorInfo";
import Navbar from "../../components/v2/homepage/Navbar";
import OverlayComponent from "../../components/v2/homepage/FloatingMenu";
import Journey from "../../components/v2/Journey";
import TextInfo from "../../components/v2/TextInfo";
import Properties from "../../components/v2/Properties";
import BillOfMaterials from "../../components/v2/BillOfMaterials";
import CareInstructions from "../../components/v2/CareInstructions";
import MaintenanceDiscard from "../../components/v2/MaintenanceDiscard";
import NavbarDesktop from "../../components/desktop/NavbarDesktop";
import FooterDesktop from "../../components/desktop/FooterDesktop";
//imports dos jsons para mapear tradução
import countries from "./jsons/countries.json";
import careInstructionsJson from "./jsons/care_instructions.json";
//Icones corrigidos
import BillOfMaterialsIcon from "../../img/BillOfMaterials.svg";
import CareInstructionsIcon from "../../img/CareInstructions.svg";
import PropertiesIcon from "../../img/Properties.svg";
import RepairDiscardIcon from "../../img/RepairDiscard.svg";
import DppLogoDesktopProduct from "../../img/dpplogoproductdesktop.svg";
import arrow_left from "../../img/arrow_left_new.svg";
import dpplogooverlay from "../../img/dpp-logo.png";
import CompositionBarChart from "../../components/v2/CompositionBarChart.jsx";
import ZaraTabs from "../../components/v2/ZaraTabs.jsx";

import SurveyNudge from "./SurveyNudge";

const CompositionGraphNew = React.lazy(() => import("../../components/v2/CompositionGraphNew.jsx"));
const CertificationsNew = React.lazy(() => import("../../components/desktop/CertificationsNew"));
const CertificationsDesktop = React.lazy(() => import("../../components/desktop/CertificationsDesktop"));
const CompanyCertificatesPopup = React.lazy(() => import("../../components/v2/CompanyCertificatesPopup"));
const GeoMap = React.lazy(() => import("../../components/v2/GeoMap"));

// In-memory cache: avoids repeat API calls within the same session
const productCache = new Map();

const categoryDefinitionEndpoints = {
  type: "https://bio.infos.pt/api/defs/product/type",
  fit: "https://bio.infos.pt/api/defs/product/fit",
  category: "https://bio.infos.pt/api/defs/product/category",
  fashion: "https://bio.infos.pt/api/defs/product/fashion",
  color: "https://bio.infos.pt/api/defs/product/color",
  season: "https://bio.infos.pt/api/defs/product/season",
};

const normalizeDefinitionLanguage = (language) => {
  const normalized = (language || "en").split("-")[0].toLowerCase();
  if (normalized === "country_prt") return "pt";
  if (normalized === "country_gbr") return "en";
  return normalized;
};

const getLocalizedDefinitionValue = (descriptions, language) => {
  if (!Array.isArray(descriptions)) return "";
  const wanted = normalizeDefinitionLanguage(language);
  const getValue = (code) => descriptions.find(
    (item) => normalizeDefinitionLanguage(item?.language) === code
      && typeof item?.value === "string"
      && item.value.trim()
      && item.value.trim() !== "-",
  )?.value || "";
  return getValue(wanted) || getValue("en");
};

export const getDefinitionDescription = (definitions, code, language) => {
  if (code === null || code === undefined || code === "" || !Array.isArray(definitions)) return "-";
  const descriptions = definitions
    .filter((definition) => definition?.code === code)
    .flatMap((definition) => Array.isArray(definition.description) ? definition.description : []);
  return getLocalizedDefinitionValue(descriptions, language) || "-";
};

export const getProductTypeDescription = (types, code, language) => {
  const options = Array.isArray(types)
    ? types.flatMap((type) => Array.isArray(type?.options) ? type.options : [])
    : [];
  return getDefinitionDescription(options, code, language);
};

export const getDefinitionListDescription = (definitions, codes, language) => {
  const normalizedCodes = Array.isArray(codes)
    ? codes
    : codes === null || codes === undefined || codes === ""
      ? []
      : [codes];
  const descriptions = normalizedCodes
    .map((code) => getDefinitionDescription(definitions, code, language))
    .filter((description) => description !== "-");
  return descriptions.length ? descriptions.join(", ") : "-";
};

export const getCategoryDescriptions = (definitions, properties = {}, language) => ({
  type: getProductTypeDescription(definitions.type, properties.type, language),
  fit: getDefinitionDescription(definitions.fit, properties.fit, language),
  category: getDefinitionDescription(definitions.category, properties.collection, language),
  fashion: getDefinitionDescription(definitions.fashion, properties.fashion, language),
  color: getDefinitionListDescription(definitions.color, properties.color, language),
  season: getDefinitionListDescription(definitions.season, properties.season, language),
});

const formatDuration = (ms) => {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(2)} s`;
  return `${(ms / 60000).toFixed(1)} min`;
};

//pagina para mostrar o produto e todas as informações do mesmo
//será esta a pagina que recebe todos os componentes
//contem função para verificar a existencia do produto a apresentar
const Product = ({ productList = [], productListEvent, hasTokenEvent }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0].toLowerCase();
  const [queryParameters] = useSearchParams();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const touchAreaRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);
  const requestedProductId = queryParameters.get("productId");
  const params = new URLSearchParams(location.search);
  const [isMobile, setIsMobile] = useState(() => {
    // Inicializa o estado com base na verificação de mídia
    if (typeof window !== "undefined") {
      return window.matchMedia("(max-width: 768px)").matches;
    }
    return false;
  });

  const [isSimilarProductsOpen, setSimilarProductsOpen] = useState(false);
  const [isPageChangeAllowed, setIsPageChangeAllowed] = useState(true);

  const [showOverlay, setShowOverlay] = useState(false);
  const [isOverlayClickable, setIsOverlayClickable] = useState(false);
  const overlayRef = useRef(null);
  const overlayCloseRef = useRef(null);
  const overlayPreviousFocusRef = useRef(null);
  const desktopOverlayPreviousFocusRef = useRef(null);
  const exploreButtonRef = useRef(null);
  const mapPreviousFocusRef = useRef(null);
  const pageHeadingRef = useRef(null);

  const containerRefTop = useRef(null);
  const productLoadStartRef = useRef(null);
  const ttuiMeasuredRef = useRef(false);
  const ttfiMeasuredRef = useRef(false);

  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [productLoadError, setProductLoadError] = useState("");
  const [categoryDefinitions, setCategoryDefinitions] = useState({});
  /* console.log("PRODUTO:", product); */
  const [shouldDisplayIndexTab, setShouldDisplayIndexTab] = useState(true);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isCertificatesModalOpen, setIsCertificatesModalOpen] = useState(false);
  const [isTextInfoModalOpen, setIsTextInfoModalOpen] = useState(false);
  const [popupText, setPopupText] = useState("");
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;

  const handleCompanyClick = (company) => {
    setSelectedCompany(company);
    setIsCertificatesModalOpen(true);
  };

  const handleCloseCertificatesModal = () => {
    setIsCertificatesModalOpen(false);
    setSelectedCompany(null);
  };

  const handleOpenTextPopup = (text) => {
  setPopupText(text);
  setIsTextInfoModalOpen(true);
};

  const renderCertificationsDesktopFallback = () => (
    <div className="certifications-desktop-container">
      <div className="certifications-desktop-title">{t('certifications')}</div>
    </div>
  );

  const renderCertificationsTabFallback = () => null;

    const handleCloseTextInfoModal = () => {
    setIsTextInfoModalOpen(false);
  setPopupText("");
  };
  


  const getByCountryLang = (items) => {
    if (!Array.isArray(items) || items.length === 0) return "";

    const normalised = items.map((it) => ({
      ...it,
      value: it.value ?? it.description ?? "",
    }));

    const wanted = (i18n.language || "en").split("-")[0].toLowerCase();

    const getLangFromCountryCode = (countryCode) => {
      const c = countries.find((x) => x.code === countryCode);
      return (c?.lang || "").toLowerCase();
    };

    const match = normalised.find(
      (it) => getLangFromCountryCode(it.language) === wanted
    );
    if (match?.value) return match.value;

    const fallbackEn = normalised.find(
      (it) => getLangFromCountryCode(it.language) === "en"
    );
    if (fallbackEn?.value) return fallbackEn.value;

    return normalised[0]?.value || "";
  };

  const getNameByCountryLang = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";

  const wanted = (i18n.language || "en").split("-")[0].toLowerCase();

  const getLangFromCountryCode = (countryCode) => {
    const c = countries.find((x) => x.code === countryCode);
    return (c?.lang || "").toLowerCase();
  };

  const match = items.find(
    (it) => getLangFromCountryCode(it.language) === wanted
  );
  if (match?.description) return match.description;

  const fallbackEn = items.find(
    (it) => getLangFromCountryCode(it.language) === "en"
  );
  if (fallbackEn?.description) return fallbackEn.description;

  return items[0]?.description || "";
};

  //GET DO PRODUTO PELO ENDPOINT E DEPOIS PRODUCTLIST
  useEffect(() => {
    const productId = queryParameters.get("productId");
    if (!productId) {
      setProduct(null);
      setActiveTab(1);
      setCurrentVisibleSlide(0);
      setProductLoadError(t("product-identification-error"));
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setProduct(null);
    setActiveTab(1);
    setCurrentVisibleSlide(0);

    console.log(`[Product] ⏳ A carregar produto: ${productId}`);
    const t0 = performance.now();
    productLoadStartRef.current = t0;
    ttuiMeasuredRef.current = false;
    ttfiMeasuredRef.current = false;
    performance.mark(`product:${productId}:start`);
    setIsLoading(true);
    setProductLoadError("");

    const applyProductData = (data, source) => {
      setProduct(data);
      setProductLoadError("");
      setShouldDisplayIndexTab(
        data?.dpp?.product_info?.type !== "DPP_TYPE_NO_SCORE",
      );
      console.log(`[Product] ✅ Dados ${source} aplicados — ${(performance.now() - t0).toFixed(0)}ms`);
    };

    const loadFullProduct = () =>
      fetch(`/products/${productId}.json`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (cancelled) return null;
          console.log(`[Product] ✅ JSON carregado — ${(performance.now() - t0).toFixed(0)}ms`);
          productCache.set(productId, data);
          applyProductData(data, "completo");
          setIsLoading(false);
          console.log(`[Product] 🟢 Spinner REMOVIDO — ${(performance.now() - t0).toFixed(0)}ms`);
          return data;
        });

    // 1. Cache hit — instant (same-session repeat visit)
    if (productCache.has(productId)) {
      const cached = productCache.get(productId);
      console.log(`[Product] ✅ Cache hit — ${(performance.now() - t0).toFixed(0)}ms`);
      applyProductData(cached, "cache");
      setIsLoading(false);
      console.log(`[Product] 🟢 Spinner REMOVIDO (cache) — ${(performance.now() - t0).toFixed(0)}ms`);
      return;
    }

    // 2. Carrega JSON completo (já leve com thumbnailBlur).
    console.log(`[Product] 🌐 A carregar /products/${productId}.json`);
    loadFullProduct()
      .catch(() => {
        if (cancelled) return;
        setProductLoadError(t("product-load-fallback"));
        console.warn(`[Product] ⚠️ /products/${productId}.json falhou — a tentar ProductList`);
        // 3. Fallback: procura no ProductList array
        if (productList.length > 0) {
          const found = productList.find((p) => p.id === productId);
          if (found) {
            console.log(`[Product] ✅ Encontrado em ProductList — ${(performance.now() - t0).toFixed(0)}ms`);
            productCache.set(productId, found);
            applyProductData(found, "ProductList");
            setIsLoading(false);
            console.log(`[Product] 🟢 Spinner REMOVIDO (ProductList) — ${(performance.now() - t0).toFixed(0)}ms`);
            return;
          }
        }
        // 4. Último recurso: API externa
        console.warn(`[Product] ⚠️ A tentar API externa`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 7000);
        fetch(`https://bio.infos.pt/api/dpp?productID=${productId}`, { signal: controller.signal })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          })
          .then((data) => {
            clearTimeout(timeoutId);
            if (cancelled) return;
            console.log(`[Product] ✅ API externa respondeu — ${(performance.now() - t0).toFixed(0)}ms`);
            productCache.set(productId, data);
            applyProductData(data, "API externa");
            setIsLoading(false);
            console.log(`[Product] 🟢 Spinner REMOVIDO (API externa) — ${(performance.now() - t0).toFixed(0)}ms`);
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            if (!cancelled && err.name !== "AbortError") {
              console.error(`[Product] ❌ Todas as fontes falharam — ${(performance.now() - t0).toFixed(0)}ms`, err);
            }
            if (!cancelled) {
              setProductLoadError(t("product-load-error"));
              setIsLoading(false);
            }
          });
      });

    return () => { cancelled = true; };
  }, [queryParameters, productList]);

  useEffect(() => {
    const productId = queryParameters.get("productId");
    if (!productId || !product || isLoading || ttuiMeasuredRef.current) return;

    ttuiMeasuredRef.current = true;
    const ttui = productLoadStartRef.current
      ? performance.now() - productLoadStartRef.current
      : 0;

    try {
      const startMark = `product:${productId}:start`;
      const ttuiMark = `product:${productId}:ttui`;
      const measureName = `TTUI:product:${productId}`;
      performance.mark(ttuiMark);
      performance.measure(measureName, startMark, ttuiMark);
    } catch (err) {
      // Performance marks are diagnostic-only; never block product rendering.
    }

    window.__DPP_METRICS__ = {
      ...(window.__DPP_METRICS__ || {}),
      [productId]: {
        ...((window.__DPP_METRICS__ || {})[productId] || {}),
        ttuiMs: Math.round(ttui),
        ttui: formatDuration(ttui),
        measuredAt: new Date().toISOString(),
      },
    };

    console.log(`[Product] 📊 TTUI ${productId} — ${formatDuration(ttui)}`);
  }, [product, isLoading, queryParameters]);

  useEffect(() => {
    const productId = queryParameters.get("productId");
    const hasFinalImage = Array.isArray(product?.dpp?.product_info?.img)
      && product.dpp.product_info.img.length > 0;

    if (!productId || !hasFinalImage || ttfiMeasuredRef.current) return;

    requestAnimationFrame(() => {
      if (ttfiMeasuredRef.current) return;

      ttfiMeasuredRef.current = true;
      const finalImageSourceReady = productLoadStartRef.current
        ? performance.now() - productLoadStartRef.current
        : 0;

      try {
        const startMark = `product:${productId}:start`;
        const sourceReadyMark = `product:${productId}:final-image-source-ready`;
        const measureName = `FinalImageSourceReady:product:${productId}`;
        performance.mark(sourceReadyMark);
        performance.measure(measureName, startMark, sourceReadyMark);
      } catch (err) {
        // Performance marks are diagnostic-only; never block product rendering.
      }

      window.__DPP_METRICS__ = {
        ...(window.__DPP_METRICS__ || {}),
        [productId]: {
          ...((window.__DPP_METRICS__ || {})[productId] || {}),
          finalImageSourceReadyMs: Math.round(finalImageSourceReady),
          finalImageSourceReady: formatDuration(finalImageSourceReady),
          finalImageSourceReadyMeasuredAt: new Date().toISOString(),
        },
      };

      console.log(`[Product] 🖼️ Fonte da imagem final pronta ${productId} — ${formatDuration(finalImageSourceReady)}`);
    });
  }, [product, queryParameters]);

  useEffect(() => {
    if (currentPage > 0) {
      setShowOverlay(false);
      setIsOverlayClickable(false);
      sessionStorage.setItem("hasSeenOverlay", "true");
      return undefined;
    }

    if (!isMobile || sessionStorage.getItem("hasSeenOverlay")) {
      setShowOverlay(false);
      setIsOverlayClickable(false);
      return undefined;
    }

    let clickableTimer;
    const overlayTimer = setTimeout(() => {
      if (currentPageRef.current !== 0 || sessionStorage.getItem("hasSeenOverlay")) return;

      setShowOverlay(true);
      clickableTimer = setTimeout(() => {
        if (currentPageRef.current === 0 && !sessionStorage.getItem("hasSeenOverlay")) {
          setIsOverlayClickable(true);
        }
      }, 2000);
    }, 3000);

    return () => {
      clearTimeout(overlayTimer);
      clearTimeout(clickableTimer);
    };
  }, [currentPage, isMobile]);

  const [showOverlayDesktop, setShowOverlayDesktop] = useState(false);
  const [isOverlayClickableDesktop, setIsOverlayClickableDesktop] =
    useState(false);

  useEffect(() => {
    if (currentPage > 0) {
      setShowOverlayDesktop(false);
      setIsOverlayClickableDesktop(false);
      sessionStorage.setItem("hasSeenOverlay", "true");
      return undefined;
    }

    if (isMobile || sessionStorage.getItem("hasSeenOverlay")) {
      setShowOverlayDesktop(false);
      setIsOverlayClickableDesktop(false);
      return undefined;
    }

    let clickableTimer;
    const overlayTimer = setTimeout(() => {
      if (currentPageRef.current !== 0 || sessionStorage.getItem("hasSeenOverlay")) return;

      setShowOverlayDesktop(true);
      clickableTimer = setTimeout(() => {
        if (currentPageRef.current === 0 && !sessionStorage.getItem("hasSeenOverlay")) {
          setIsOverlayClickableDesktop(true);
        }
      }, 1000);
    }, 3000);

    return () => {
      clearTimeout(overlayTimer);
      clearTimeout(clickableTimer);
    };
  }, [currentPage, isMobile]);

  useEffect(() => {
    // Function to hide overlay on click if overlay is clickable
    const handleClick = () => {
      if (isOverlayClickableDesktop) {
        setShowOverlayDesktop(false);
        setIsOverlayClickableDesktop(false); // Reset click enabled state
        sessionStorage.setItem("hasSeenOverlay", "true"); // Mark overlay as shown in session storage
      }
    };

    // Add event listener when overlay is shown
    if (showOverlayDesktop) {
      document.addEventListener("click", handleClick);
    }

    // Cleanup event listener
    return () => document.removeEventListener("click", handleClick);
  }, [showOverlayDesktop, isOverlayClickableDesktop]);

  const handleGoToPage = () => {
    setShowOverlay(false);
    setIsOverlayClickable(false);
    setShowOverlayDesktop(false); // Hide the overlay if clicked
    setIsOverlayClickableDesktop(false);
    sessionStorage.setItem("hasSeenOverlay", "true");
    goToPage(shouldDisplayIndexTab ? 1 : 2);
  };

  const handleDismissDesktopOverlay = () => {
    setShowOverlayDesktop(false);
    setIsOverlayClickableDesktop(false);
    sessionStorage.setItem("hasSeenOverlay", "true");
    requestAnimationFrame(() => desktopOverlayPreviousFocusRef.current?.focus?.());
  };

  // Function to handle overlay dismissal after it appears
  const handleDismissOverlay = () => {
    setShowOverlay(false);
    setIsOverlayClickable(false);
    sessionStorage.setItem("hasSeenOverlay", "true");
    overlayPreviousFocusRef.current?.focus?.();
  };

  useEffect(() => {
    const content = document.querySelector(".product-container");
    const footer = document.querySelector(".navbar-footer");
    if (!showOverlay) {
      [content, footer].forEach((element) => {
        if (element) {
          element.inert = false;
          element.removeAttribute("aria-hidden");
        }
      });
      return undefined;
    }

    overlayPreviousFocusRef.current = document.activeElement;
    [content, footer].forEach((element) => {
      if (element) {
        element.inert = true;
        element.setAttribute("aria-hidden", "true");
      }
    });
    overlayCloseRef.current?.focus();

    const handleModalKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleDismissOverlay();
        return;
      }
      if (event.key !== "Tab" || !overlayRef.current) return;
      const focusable = overlayRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleModalKeyDown);
    return () => document.removeEventListener("keydown", handleModalKeyDown);
  }, [showOverlay, currentPage]);

  useEffect(() => {
    if (!showOverlayDesktop) return undefined;

    desktopOverlayPreviousFocusRef.current = document.activeElement;
    const handleDesktopOverlayKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      handleDismissDesktopOverlay();
    };

    document.addEventListener("keydown", handleDesktopOverlayKeyDown);
    return () => document.removeEventListener("keydown", handleDesktopOverlayKeyDown);
  }, [showOverlayDesktop]);

  useEffect(() => {
    if (currentPage === 0 || !pageHeadingRef.current) return;
    pageHeadingRef.current.focus();
  }, [currentPage]);

  // Quando é mobile e há IndexTab ativo, removemos a página "Index" (era a page 1)
  const isMobileIndexRemoved = isMobile && shouldDisplayIndexTab;

  // pageKey é a página "real" que vai cair nas tuas condições existentes
  // (salta a antiga page 1 no mobile, empurrando 1->2, 2->3, 3->4)
  const pageKey =
    isMobileIndexRemoved && currentPage > 0 ? currentPage + 1 : currentPage;

  //num max de paginas
  const maxPages = isMobileIndexRemoved
    ? 4
    : 3 + (shouldDisplayIndexTab ? 2 : 1);
  // Ajuste conforme o número de páginas

  //-----------------------------

  const goToPage = (pageIndex) => {
    setCurrentPage(pageIndex);
  };

  const handlePageUp = () => {
    setCurrentPage((prevPage) => {
      let newPage = prevPage - 1;
      /* if (!shouldDisplayIndexTab && newPage === 1) {
        newPage = 0; // Skip index 1
      } */
      return newPage >= 0 ? newPage : 0;
    });
  };

  const handlePageDown = () => {
    setCurrentPage((prevPage) => {
      let newPage = prevPage + 1;
      /* if (!shouldDisplayIndexTab && newPage === 1) {
        newPage = 2; // Skip index 1
      } */
      return newPage < maxPages ? newPage : maxPages - 1;
    });
  };

  // CASO EXISTA TOKEN
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.has("expositionMude")) {
      const expositionMude = params.get("expositionMude");
      if (expositionMude === "true") {
        const storedToken = JSON.parse(localStorage.getItem("authToken"));
        const newToken = generateToken();
        hasTokenEvent(true, expirationDate);
        params.delete("expositionMude");
        navigate(`${location.pathname}?${params.toString()}`, {
          replace: true,
        });
      }
    } else {
      /*    localStorage.removeItem("authToken"); */
      /*   console.log("Parâmetro expositionMude não encontrado."); */
    }
  }, []);

  // <-----------------------GERAR TOKEN CASO PRODUTO SEJA DA LISTA------------------------------------------>
  const startDate = new Date();
  const expirationDate = new Date(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
    23,
    59,
    59,
    999,
  );

  const generateToken = () => {
    const token = {
      value: "your-unique-token",
      startDate: startDate.getTime(),
      expiration: expirationDate.getTime(),
    };

    localStorage.setItem("authToken", JSON.stringify(token));
    return token;
  };

  const isTokenValid = (token) => {
    const currentTime = new Date().getTime();
    return token && token.expiration > currentTime; // Verifica se o token existe e ainda não expirou
  };
  // <-------------------------------------------token----------------->

  // scroll a paginas do conteudo do lado direito
  const containerRef = useRef(null);

  useEffect(() => {
    const preventPullToRefresh = (e) => {
      if (e.touches.length > 1) {
        // If there are more than one touches, it's a multi-touch gesture, so we ignore it.
        return;
      }

      // Allow native touch scroll inside any scrollable element (e.g. popups)
      let node = e.target;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        if (
          ["auto", "scroll"].includes(style.overflowY) &&
          node.scrollHeight > node.clientHeight
        ) {
          return;
        }
        node = node.parentElement;
      }

      if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
        return;
      }

      if (e.touches[0].clientY > 0) {
        // Prevent the pull-to-refresh action
        e.preventDefault();
      }
    };

    // Add the event listener
    document.addEventListener("touchmove", preventPullToRefresh, {
      passive: false,
    });

    // Cleanup the event listener when the component unmounts
    return () => {
      document.removeEventListener("touchmove", preventPullToRefresh);
    };
  }, []);

  // Função para manipular os eventos scroll/gesture (page change)
  const debouncedHandleWheel = debounce((event) => {
    const rightContainer = containerRef.current;

    if (
      !rightContainer ||
      !isPageChangeAllowed ||
      /* isSimilarProductsOpen || */ isMapExpanded
    )
      return;

    const isScrollingDown = event.deltaY > 0;
    const currentScrollTop = rightContainer.scrollTop;

    const adjustedDeltaY =
      event.deltaMode === 0 ? event.deltaY / 50 : event.deltaY;

    if (event.deltaMode === 0 && Math.abs(event.deltaY) < 50) {
      return;
    }

    if (rightContainer.scrollHeight > rightContainer.clientHeight) {
      if (
        isScrollingDown &&
        currentScrollTop + rightContainer.clientHeight >=
          rightContainer.scrollHeight - 1
      ) {
        setCurrentPage((prevPage) => {
          const nextPage = prevPage + 1;
          if (!shouldDisplayIndexTab && nextPage === 1) {
            return 2; // Skip index 1
          }
          return nextPage < 4 ? nextPage : prevPage;
        });
        event.preventDefault();
      } else if (!isScrollingDown && currentScrollTop === 0) {
        setCurrentPage((prevPage) => {
          const previousPage = prevPage - 1;
          if (!shouldDisplayIndexTab && previousPage === 1) {
            return 2; // Skip index 1
          }
          return Math.max(previousPage, 1); // Prevent going below page 1
        });
        event.preventDefault();
      }
    } else {
      if (isScrollingDown) {
        setCurrentPage((prevPage) => {
          const nextPage = prevPage + 1;
          if (!shouldDisplayIndexTab && nextPage === 1) {
            return 2; // Skip index 1
          }
          return nextPage < 4 ? nextPage : prevPage;
        });
        event.preventDefault();
      } else {
        setCurrentPage((prevPage) => {
          const previousPage = prevPage - 1;
          if (!shouldDisplayIndexTab && previousPage === 1) {
            return 2; // Skip index 1
          }
          return Math.max(previousPage, 1); // Prevent going below page 1
        });
        event.preventDefault();
      }
    }

    setIsPageChangeAllowed(false);
    setTimeout(() => {
      setIsPageChangeAllowed(true);
    }, 200);
  }, 100);

  // Função para manipular eventos do teclado (setas para cima/baixo)
  /* const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" || event.key === "PageDown") {
      handleArrowClick("down");
      event.preventDefault();
    } else if (event.key === "ArrowUp" || event.key === "PageUp") {
      handleArrowClick("up");
      event.preventDefault();
    }
  }; */

  const handleOpenLink = (url) => {
    if (!url) return;
    const fullUrl =
      url.startsWith("http://") || url.startsWith("https://")
        ? url
        : `https://${url}`;
    window.open(fullUrl, "_blank", "noopener,noreferrer");
  };

  const handleArrowClick = (direction) => {
    const rightContainer = containerRef.current;

    // Verifica se a página está válida para alterar
    if (!rightContainer || !isPageChangeAllowed) return;

    // Verifica se a direção é "down" ou "up"
    if (direction === "down") {
      setCurrentPage((prevPage) => {
        const nextPage = prevPage + 1;
        if (!shouldDisplayIndexTab && nextPage === 1) {
          return 2; // Skip index 1
        }
        if (nextPage < 4) {
          return nextPage;
        } else if (prevPage === 3) {
          setSimilarProductsOpen(true); // Open similar products if on page 3
          return prevPage;
        }
        return prevPage;
      });
    } else if (direction === "up") {
      setCurrentPage((prevPage) => {
        const previousPage = prevPage - 1;
        if (!shouldDisplayIndexTab && previousPage === 1) {
          return 2; // Skip index 1
        }
        return Math.max(previousPage, 1); // Prevent going below page 1
      });
    }

    setIsPageChangeAllowed(false);
    setTimeout(() => {
      setIsPageChangeAllowed(true);
    }, 100);
  };

  useEffect(() => {
    if (showOverlayDesktop && containerRefTop.current) {
      containerRefTop.current.scrollTop = 0; // Reset scroll to the top
    }
  }, [showOverlayDesktop]); // Trigger effect when showOverlayDesktop changes

  useEffect(() => {
    const rightContainer = containerRef.current;

    if (rightContainer) {
      // Add event listeners for scroll/swipe and clicks
      rightContainer.addEventListener("wheel", debouncedHandleWheel);
    }

    return () => {
      if (rightContainer) {
        rightContainer.removeEventListener("wheel", debouncedHandleWheel);
      }
    };
  }, [debouncedHandleWheel /* isSimilarProductsOpen */]);

  // Adiciona o listener de eventos de teclado ao carregar o componente
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isPageChangeAllowed || event.defaultPrevented) return;
      if (event.target.closest?.(
        'input, button, [role="button"], a, select, textarea, [contenteditable="true"], [role="menu"], [role="tab"], [role="dialog"], [role="option"], [role="checkbox"], [role="switch"]',
      )) return;

      if (event.key === "ArrowDown" || event.key === "PageDown") {
        setCurrentPage((prevPage) => {
          const nextPage = prevPage + 1;
          if (!shouldDisplayIndexTab && nextPage === 1) {
            return 2; // Pula índice 1
          }
          if (nextPage < 4) {
            return nextPage;
          } else if (prevPage === 3) {
            setSimilarProductsOpen(true);
            return prevPage;
          }
          return prevPage;
        });
        event.preventDefault();
      } else if (event.key === "ArrowUp" || event.key === "PageUp") {
        setCurrentPage((prevPage) => {
          const previousPage = prevPage - 1;
          if (!shouldDisplayIndexTab && previousPage === 1) {
            return 2;
          }
          return Math.max(previousPage, 1);
        });
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPageChangeAllowed, shouldDisplayIndexTab]); // 🔹 Agora sempre terá os valores corretos

  useEffect(() => {
    // Verifica se o objeto matchMedia está disponível
    if (typeof window === "undefined") {
      return;
    }
    const mediaQueryList = window.matchMedia("(max-width: 768px)");
    // Função para atualizar o estado com base na mudança da condição de mídia
    const handleChange = (e) => {
      setIsMobile(e.matches);
    };
    // Adiciona o ouvinte para mudanças
    mediaQueryList.addEventListener("change", handleChange);
    // Verifica a condição inicial
    handleChange(mediaQueryList);
    // Função de limpeza para remover o ouvinte
    return () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
  }, []);

  const handleBackClick = () => {
    if (currentPage === 0) {
      navigate("/view");
    } else {
      setCurrentPage(0);
    }
  };

  const handleMainpage = () => {
    setCurrentPage(0);
  };

  const [isMenuOpen, setMenuOpen] = useState(false);
  const handleOpenMenu = () => {
    setMenuOpen(true);
  };
  const handleCloseMenu = () => {
    setMenuOpen(false);
  };
  const carouselRef = useRef();
  const handleNextClick = () => {
    carouselRef.current.goToNextSlide();
  };
  const handlePrevClick = () => {
    carouselRef.current.goToPrevSlide();
  };

  const handlers = useSwipeable({
    onSwipedLeft: handleNextClick,
    onSwipedRight: handlePrevClick,
    preventDefaultTouchmoveEvent: false,
    trackMouse: true,
  });

  const handlersTab = useSwipeable({
    onSwipedLeft: () =>
      setActiveTab((prev) => {
        return prev < 2 ? prev + 1 : prev;
      }),
    onSwipedRight: () =>
      setActiveTab((prev) => {
        if (shouldDisplayIndexTab) {
          return prev > 0 ? prev - 1 : prev; // Tabs 0, 1 e 2
        } else {
          return prev > 1 ? prev - 1 : prev; // Tabs 1 e 2
        }
      }),
    preventDefaultTouchmoveEvent: true,
  });
  const handleTabKeyDown = (event) => {
    if (!event.target.matches('[role="tab"]')) return;
    const tabs = Array.from(event.currentTarget.querySelectorAll('[role="tab"]'));
    const index = tabs.indexOf(event.target);
    let nextIndex = index;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
    if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = tabs.length - 1;
    if (nextIndex === index) return;
    event.preventDefault();
    tabs[nextIndex].click();
    tabs[nextIndex].focus();
  };

  //PAGINA 5
  const [currentVisibleSlide, setCurrentVisibleSlide] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    Object.entries(categoryDefinitionEndpoints).forEach(async ([key, url]) => {
      let definitions = [];
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!Array.isArray(data)) throw new Error("Resposta inválida: esperado array");
        definitions = data;
        console.info(`[Product][defs] ${key}: ${data.length} definições carregadas`, url);
      } catch (error) {
        if (!controller.signal.aborted) console.warn(`[Product][defs] ${key}: falha ao carregar`, url, error);
      }
      if (!cancelled) {
        setCategoryDefinitions((current) => ({ ...current, [key]: definitions }));
      }
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  //-----------------VERIFICAÇÕES--------------------
  const getCurrentLanguage = () => (
    i18n.resolvedLanguage
    || i18n.language
    || "en"
  ).toLowerCase();

   const getCountryDescription = (code) => {
    const country = countries.find((country) => country.code === code);
    if (!country) return "Unknown Country";
    
    // Usa o idioma atual do i18n
    const currentLang = getCurrentLanguage();
    return country.description[currentLang] || country.description.en;
  };

  const getCareInstructionLabel = (code) => {
  const item = careInstructionsJson.find((x) => x.code === code);
  if (!item?.description) return code;
  return item.description[lang] || item.description.en || code;
};
  //-------------FIM DAS VERIFICAÇÕES----------------

  const handleToggleMap = () => {
    setIsMapExpanded(!isMapExpanded);
  };

  useEffect(() => {
    if (!isMapExpanded) return undefined;

    mapPreviousFocusRef.current = document.activeElement;
    const handleMapKeyDown = (event) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.preventDefault();
      setIsMapExpanded(false);
      requestAnimationFrame(() => mapPreviousFocusRef.current?.focus?.());
    };

    document.addEventListener("keydown", handleMapKeyDown);
    return () => document.removeEventListener("keydown", handleMapKeyDown);
  }, [isMapExpanded]);

  const [startY, setStartY] = useState(0);
  const swipeThreshold = 70; // Adjust this threshold as needed

  useEffect(() => {
    // Simulate loading time, replace with your actual loading logic
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 0);
    return () => clearTimeout(timeout); // Cleanup on component unmount
  }, []);

  // >----------------------- FIM GERAR TOKEN CASO PRODUTO SEJA DA LISTA------------------------------------------<

  const returnToMainPage = () => {
    setCurrentPage(0); // define a pagina para a primeira (main product)
  };

  const handleTouchStart = (event) => {
    if (!isMobile) return; // Only run if isMobile is true
    if (!isMenuOpen) {
      setStartY(event.touches[0].clientY);
    } else {
      // Ignore swipe gestures when the menu is open
      event.preventDefault();
    }
  };

  const handleTouchMove = (event) => {
    if (!isMobile || !startY) return; // Only run if isMobile is true and startY is set

    const deltaY = event.touches[0].clientY - startY;
    const scrollArea = scrollAreaRef.current;
    const touchArea = touchAreaRef.current;

    // Prevent swipes within touchArea
    if (touchArea && touchArea.contains(event.target)) {
      return;
    }

    // Check if the user is scrolling inside scrollAreaRef
    if (scrollArea && scrollArea.contains(event.target)) {
      const isAtTop = scrollArea.scrollTop === 0;
      const isAtBottom =
        scrollArea.scrollTop + scrollArea.clientHeight ===
        scrollArea.scrollHeight;

      // Allow scrolling inside scrollArea
      if (!(isAtTop && deltaY > 0) && !(isAtBottom && deltaY < 0)) {
        // User is scrolling inside the scroll area, do not trigger page swipe
        return;
      }
    }

    // Handle swipe actions when outside scrollArea or at top/bottom of scrollArea
    if (Math.abs(deltaY) > swipeThreshold) {
      if (deltaY < 0) {
        handlePageDown();
      } else {
        handlePageUp();
      }
      setStartY(0);
    }
  };

  const handleTouchMoveDebounced = debounce(handleTouchMove, 10);

  const handleTouchEnd = () => {
    if (!isMobile) return; // Only run if isMobile is true
    setStartY(0); // Reset startY on touch end
  };

  const getPageTitle = (page) => {
  // Mobile + shouldDisplayIndexTab => removemos a página "Index"
  // (page aqui deve ser o pageKey)
  if (isMobileIndexRemoved) {
    switch (page) {
      case 2:
        return t("detailed-index"); // currentPage=1 -> pageKey=2
      case 3:
        return t("the-journey");    // currentPage=2 -> pageKey=3
      case 4:
        return t("additional-information"); // currentPage=3 -> pageKey=4
      default:
        return "";
    }
  }

  // Desktop com 4 páginas (sem "Additional Information")
  // Nota: isto acontece quando shouldDisplayIndexTab é false (maxPages===4),
  // ou seja, mantém a lógica antiga do teu getPageTitle.
  if (maxPages === 4 && !isMobile) {
    switch (page) {
      case 1:
        return t("index");
      case 2:
        return t("detailed-index");
      case 3:
        return t("the-journey");
      default:
        return "";
    }
  }

  if (maxPages === 4 && isMobile) {
    switch (page) {
      case 1:
        return t("detailed-index"); // currentPage=1 -> pageKey=2
      case 2:
        return t("the-journey");    // currentPage=2 -> pageKey=3
      case 3:
        return t("additional-information"); // currentPage=3 -> pageKey=4
      default:
        return "";
    }
  }

  // Estrutura default quando maxPages === 5 (com "Additional Information")
  switch (page) {
    case 1:
      return t("index");
    case 2:
      return t("detailed-index");
    case 3:
      return t("the-journey");
    case 4:
      return t("additional-information");
    default:
      return "";
  }
};


  const pageTitle = getPageTitle(pageKey);

  let pageContent;
  const dataDetailedIndex = useMemo(() => {
    if (!product?.dpp) return [];

    return [
      {
        id: 0,
        title: t('water-consumption'),
        description: {
          indicator: t('water-consumption-description-indicator'),
          indicator_desktop:
            product.dpp.product_info.detailed_index.water_consumption.value +
            " " + t('water-consumption-description-indicator-desktop'),
          complexgraph:
            t('complexgraph'),
          main: t('water-consumption-main'),
          complexgraph_desktop:
            t('complexgraph-desktop'),
          main_desktop:
            t('water-consumption-main-desktop'),
        },
        icon: "/images/icon_water_v2.svg",
        value: product.dpp.product_info.detailed_index.water_consumption.value,
        units: product.dpp.product_info.detailed_index.water_consumption.units,
        scale: product.dpp.product_info.detailed_index.water_consumption.scale,
        production_activities:
          product.dpp.product_info.detailed_index.water_consumption
            .production_activities,
      },
      {
        id: 1,
        title: t('carbon-footprint'),
        description: {
          indicator: t('carbon-footprint-description'),
          indicator_desktop:
            product.dpp.product_info.detailed_index.carbon_footprint.value +
            " " + t('carbon-footprint-description-desktop'),
          complexgraph:
            t('carbon-footprint-complexgraph'),
          main: t('carbon-footprint-main'),
          complexgraph_desktop:
            t('complexgraph-desktop') ,
          main_desktop:
            t('carbon-footprint-main-desktop'),
        },
        icon: "/images/icon_co2_v2.svg",
        value: product.dpp.product_info.detailed_index.carbon_footprint.value,
        units: product.dpp.product_info.detailed_index.carbon_footprint.units,
        scale: product.dpp.product_info.detailed_index.carbon_footprint.scale,
        production_activities:
          product.dpp.product_info.detailed_index.carbon_footprint
            .production_activities,
      },
      {
        id: 2,
        title: t('chemical-consumption'),
        description: {
          indicator: t('chemical-consumption-description'),
          indicator_desktop:
            product.dpp.product_info.detailed_index.chemical_products.value +
            " " + t('chemical-consumption-description-desktop'),
          complexgraph:
            t('complexgraph'),
          main: t('chemical-consumption-main'),
          complexgraph_desktop:
            t('complexgraph-desktop'),
          main_desktop:
            t('chemical-consumption-main-desktop'),
        },
        icon: "/images/icon_chemical_v2.svg",
        value: product.dpp.product_info.detailed_index.chemical_products.value,
        units: product.dpp.product_info.detailed_index.chemical_products.units,
        scale: product.dpp.product_info.detailed_index.chemical_products.scale,
        production_activities:
          product.dpp.product_info.detailed_index.chemical_products
            .production_activities,
      },
      /* {
        id: 3,
        title: t('recovered-waste'),
        description: {
          indicator: t('recovered-waste-description'),
          indicator_desktop:
            product.dpp.product_info.detailed_index.recovered_waste.value +
            " " + t('recovered-waste-description-desktop'),
          complexgraph:
            t('complexgraph'),
          main: t('recovered-waste-main'),
          complexgraph_desktop:
            t('complexgraph-desktop'),
          main_desktop:
            t('recovered-waste-main-desktop'),
        },
        icon: "/images/icon_recovered_waste_v2.svg",
        value: product.dpp.product_info.detailed_index.recovered_waste.value,
        units: product.dpp.product_info.detailed_index.recovered_waste.units,
        scale: product.dpp.product_info.detailed_index.recovered_waste.scale,
        production_activities:
          product.dpp.product_info.detailed_index.recovered_waste
            .production_activities,
      }, */
    ];
  }, [product, i18n.language]);
  const data = [
    {
      image: PropertiesIcon,
      text: t('properties'),
      id: 0,
    },
    {
      image: CareInstructionsIcon,
      text: t('care-instructions'),
      id: 1,
    },
    {
      image: RepairDiscardIcon,
      text: t('repair-and-discard'),
      id: 2,
    },
    {
      image: BillOfMaterialsIcon,
      text: t('bill-of-materials'),
      id: 3,
    },
  ];
  const weight = useMemo(() => {
    return product?.dpp?.product_info?.properties?.weight ?? "-";
  }, [product]);

  const displayWeight = weight !== "-" ? `${weight} kg` : "-";
  

  const categoriesData = useMemo(() => {
    if (!product?.dpp) return [];
    const properties = product?.dpp?.product_info?.properties || {};
    const descriptions = getCategoryDescriptions(categoryDefinitions, properties, lang);
    if (Object.keys(categoryDefinitions).length === Object.keys(categoryDefinitionEndpoints).length) {
      console.info("[Product][defs] propriedades resolvidas", {
        productId: product.id,
        language: lang,
        codes: properties,
        descriptions,
      });
    }

    return [
      { key: "type-of-product", group: "general", name: t("type-of-product"), description: descriptions.type },
      { key: "fit", group: "general", name: t("fit"), description: descriptions.fit },
      { key: "category", group: "general", name: t("category"), description: descriptions.category },
      { key: "fashion", group: "general", name: t("fashion"), description: descriptions.fashion },
      { key: "color", group: "general", name: t("color"), description: descriptions.color },
      { key: "collection", group: "general", name: t("collection"), description: descriptions.season },
      { key: "weight", group: "general", name: t("weight"), description: displayWeight },
      /* { key: "variants", group: "general", name: t("variants"), description: product.dpp.product_info.properties.number_of_variants || "-" }, */
      { key: "reciclability", group: "environmental", name: t("reciclability"), description: properties.reciclability !== null && properties.reciclability !== undefined ? (properties.reciclability ? t("yes") : t("no")) : "-" },
      { key: "microplastics", group: "environmental", name: t("microplastics"), description: properties.microplastics !== null && properties.microplastics !== undefined ? (properties.microplastics ? t("yes") : t("no")) : "-" },
      { key: "dangerous-chemicals", group: "environmental", name: t("dangerous-chemicals"), description: properties.dangerous_chemicals !== null && properties.dangerous_chemicals !== undefined ? (properties.dangerous_chemicals ? t("yes") : t("no")) : "-" },
    ];
  }, [product, displayWeight, categoryDefinitions, lang, i18n.language]);

  //Conteudo da pagina
  if (product !== null) {
    if (shouldDisplayIndexTab) {
      if (pageKey === 0 && isMobile) {
        pageContent = (
          <div className="page1-container-v2">
            <div className="header-content-v2">
              <button type="button" className="product-arrow-goback" aria-label={t("back")} onClick={handleBackClick}><img src="/images/icon_arrow_back.svg" alt="" aria-hidden="true" /></button>
              <div className="header-content-v2-content">
                <h1 style={{ margin: 0 }} className="header-content-v2-title">
                  {getNameByCountryLang(product?.dpp?.product_info?.name)}
                  {product.dpp.product_info.link && (
                    <button type="button" style={{ border: 0, padding: 0, background: "transparent" }} aria-label={t("open-product-link")} onClick={() => handleOpenLink(product.dpp.product_info.link)}><img className="link-produto" src="/images/icon_redirect.svg" alt="" aria-hidden="true" /></button>
                  )}
                </h1>
                <div className="header-content-v2-text">
                  {product.dpp.product_info.brand && (
                    <>{product.dpp.product_info.brand} · </>
                  )}
                  {t("made-in")}{" "}
                  {getCountryDescription(product.dpp.product_info.made_in)}
                </div>
              </div>
              {product.dpp.product_info.text_infos && (
                <button type="button" style={{ border: 0, padding: 0, background: "transparent" }} aria-label={t("more-product-information")} onClick={() => handleOpenTextPopup(product.dpp.product_info.text_infos)}><img className="textinfo-produto" src="/images/icon_knowmore.svg" alt="" aria-hidden="true" /></button>
              )}
            </div>
            <div className="product-maindiv-container-v2">
              <ProductGallery
                recycled={product.dpp.product_info.composition.recycled}
                compositionList={product.dpp.product_info.composition.fibers}
                certifications={product.dpp.product_info.certifications}
                productImgs={Array.isArray(product.dpp.product_info.img) ? product.dpp.product_info.img : []}
                thumbnailBlur={product.dpp.product_info.thumbnailBlur}
                productId={product.id || queryParameters.get("productId")}
                productLoadStartMs={productLoadStartRef.current}
                touchAreaRef={touchAreaRef}
                handleNavigation={returnToMainPage}
                product_info={product.dpp.product_info}
                score={product.dpp.product_info.sac}
              />
            </div>
            {shouldDisplayIndexTab ? (
              <div className="tabs-container">
                <div className="tabs-header" role="tablist" aria-label={t("product-information")} onKeyDown={handleTabKeyDown}>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-0"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 0}
                    tabIndex={activeTab === 0 ? 0 : -1}
                    className={`tab-button ${activeTab === 0 ? "active" : ""}`}
                    onClick={() => setActiveTab(0)}
                    >
                    {t("composition")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-1"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 1}
                    tabIndex={activeTab === 1 ? 0 : -1}
                    className={`tab-button ${activeTab === 1 ? "active" : ""}`}
                    onClick={() => setActiveTab(1)}
                    >
                    {t("index")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-2"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 2}
                    tabIndex={activeTab === 2 ? 0 : -1}
                    className={`tab-button ${activeTab === 2 ? "active" : ""}`}
                    onClick={() => setActiveTab(2)}
                    >
                    {t("certifications")}
                  </button>
                </div>
                <div
                  className="tabs-underline"
                  style={{ left: `${activeTab * 30.706}%` }}
                ></div>
                <div className="tabs-content" role="tabpanel" id="product-tabpanel" aria-labelledby={`product-tab-${activeTab}`} {...handlersTab}>
                  {activeTab === 0 && (
                    <div className="composition-new">
                      <div className="composition-new-circular">
                        <React.Suspense fallback={null}>
                          <CompositionGraphNew
                            recycled={
                              product.dpp.product_info.composition.recycled
                            }
                          />
                        </React.Suspense>
                      </div>
                      <div className="composition-new-bar">
                        <CompositionBarChart
                          compositions={
                            product.dpp.product_info.composition.fibers
                          }
                        />
                      </div>
                    </div>
                  )}
                  {activeTab === 1 && (
                    <div className="product-maindiv-score-v2">
                      <ScoreInfo
                        pageKey={0}
                        icon={"/images/icon_AF.svg"}
                        title={t("environmental-and-circularity-index")}
                        description={
                          "A higher rating represents a more sustainable product."
                        }
                        score={product.dpp.product_info.sac}
                      />
                    </div>
                  )}
                  {activeTab === 2 && (
                    <div className="certifications-container-desktop-new">
                      <React.Suspense fallback={null}>
                        <CertificationsNew
                          certifications={product.dpp.product_info.certifications}
                        ></CertificationsNew>
                      </React.Suspense>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="tabs-container">
                <div className="tabs-header" role="tablist" aria-label={t("product-information")} onKeyDown={handleTabKeyDown}>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-1"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 1}
                    tabIndex={activeTab === 1 ? 0 : -1}
                    className={`tab-button ${activeTab === 1 ? "active" : ""}`}
                    onClick={() => setActiveTab(1)}
                    >
                    {t("composition")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-2"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 2}
                    tabIndex={activeTab === 2 ? 0 : -1}
                    className={`tab-button ${activeTab === 2 ? "active" : ""}`}
                    onClick={() => setActiveTab(2)}
                    >
                    {t("certifications")}
                  </button>
                </div>
                <div
                  className="tabs-underline without-score"
                  style={{ left: `${(activeTab - 1) * 46.06}%` }}
                ></div>
                <div className="tabs-content" role="tabpanel" id="product-tabpanel" aria-labelledby={`product-tab-${activeTab}`} {...handlersTab}>
                  {activeTab === 1 && (
                    <div className="composition-new">
                      <div className="composition-new-circular">
                        <React.Suspense fallback={null}>
                          <CompositionGraphNew
                            recycled={
                              product.dpp.product_info.composition.recycled
                            }
                          />
                        </React.Suspense>
                      </div>
                      <div className="composition-new-bar">
                        <CompositionBarChart
                          compositions={
                            product.dpp.product_info.composition.fibers
                          }
                        />
                      </div>
                    </div>
                  )}
                  {activeTab === 2 && (
                    <div className="certifications-container-desktop-new">
                      <React.Suspense fallback={null}>
                        <CertificationsNew
                          certifications={product.dpp.product_info.certifications}
                        ></CertificationsNew>
                      </React.Suspense>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      } else if (pageKey === 0 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div
                ref={containerRefTop} // Attach the ref
                className="product-rightside-main-div"
                style={showOverlayDesktop ? { overflow: "hidden" } : {}}
              >
                {showOverlayDesktop && <div className="overlay-desktop"></div>}
                <div className="product-maindiv-score-v2-desktop">
                  <div className="box-score-container-v2-desktop">
                    <ScoreInfo
                      pageKey={0}
                      icon={"/images/icon_AF.svg"}
                      title={t('environmental-and-circularity-index')}
                      description={""}
                      score={product.dpp.product_info.sac}
                    />
                    <button
                      ref={exploreButtonRef}
                      autoFocus
                      type="button"
                      className="box-clicktosee"
                      aria-label={t("explore-dpp-cta")}
                      onClick={handleGoToPage}
                    >
                      <div className="box-clicktosee-text">
                        {" "}
                        {t('click-to-explore-our')}{" "}
                      </div>
                      <img
                        src={DppLogoDesktopProduct}
                        alt=""
                        aria-hidden="true"
                        className="box-clicktosee-img"
                      />
                      <img
                        src="/images/icon_arrow_back.svg"
                        alt=""
                        aria-hidden="true"
                        className="box-clicktosee-arrow"
                      />
                      {showOverlayDesktop && (
                        <img
                          src="/images/box-clicktosee-gif.gif"
                          alt="box-clicktosee-gif"
                          className="box-clicktosee-gif"
                        />
                      )}
                    </button>
                    {/* <div className="title-product-maindiv-score-v2-desktop">
                    <div
                      className="click-to-see-dpp-product"
                      onClick={handleGoToPage}
                    >
                      {" "}
                      Click to explore our{" "}
                    </div>
                    <img
                      src={DppLogoDesktopProduct}
                      alt="icon-arrows"
                      className="img-product-maindiv-score-v2-desktop"
                    />
                  </div> */}
                  </div>
                </div>
                <div className="certifications-container-desktop">
                  <React.Suspense fallback={renderCertificationsDesktopFallback()}>
                    <CertificationsDesktop
                      certifications={product.dpp.product_info.certifications}
                    ></CertificationsDesktop>
                  </React.Suspense>
                </div>
                {/* <div className="composition-desktop">
              <CompositionGraphDesktop
                compositionList={product.dpp.product_info.composition.fibers}
                recycled={product.dpp.product_info.composition.recycled}
              />
            </div> */}

                <div className="composition-new-container">
                  {!isMobile && (
                    <div className="certifications-desktop-title">
                      {" "}
                      {t('composition')}
                    </div>
                  )}
                  <div className="composition-new">
                    <div className="composition-new-circular">
                      <React.Suspense fallback={null}>
                        <CompositionGraphNew
                          recycled={product.dpp.product_info.composition.recycled}
                        />
                      </React.Suspense>
                    </div>
                    <div className="composition-new-bar">
                      <CompositionBarChart
                        compositions={
                          product.dpp.product_info.composition.fibers
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="additional-info-container-desktop">
                  <div className="additional-info-product-container">
                    <ZaraTabs
                      refNextBack={carouselRef}
                      data={data}
                      currentVisibleSlide={currentVisibleSlide}
                      setCurrentVisibleSlide={setCurrentVisibleSlide}
                      instanceId="desktop-additional"
                      panelId="desktop-additional-panel"
                    />
                  </div>
                  <div
                    role="tabpanel"
                    aria-labelledby={`desktop-additional-tab-${currentVisibleSlide}`}
                    className="product-maindiv-page1-v2-desktop"
                    id="desktop-additional-panel"
                    {...handlers}
                  >
                    <div className="container-arrows-content-desktop-product">
                      {currentVisibleSlide === 0 && (
                        <div className="properties">
                          {<Properties categories={categoriesData} />}
                        </div>
                      )}
                      {currentVisibleSlide === 1 && (
                        <div className="care-instructions-products">
                          <CareInstructions
                            care_instructions={product?.dpp?.product_info?.care_instructions}
                            productId={product?.id}
                          />
                        </div>
                      )}
                      {currentVisibleSlide === 2 && (
                        <div className="maintenance-discard">
                          <MaintenanceDiscard
                            repair_text={getByCountryLang(
                              product?.dpp?.product_info?.maintenance_repair_instructions
                            )}
                            discard_text={getByCountryLang(
                              product?.dpp?.product_info?.discard_instructions
                            )}
                          />
                        </div>
                      )}
                      {currentVisibleSlide === 3 && (
                        <div className="bill-of-materials">
                          {
                            <BillOfMaterials
                              data={product.dpp.product_info.bill_of_materials || []}
                            ></BillOfMaterials>
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div> 
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 1 && isMobile) {
        pageContent = (
          <div className="page2-container-v2">
            <div className="product-maindiv-page">
              <div className="div-score-sac-ses-v2">
                <ScoreInfo
                  pageKey={1}
                  icon={"/images/icon_AF.svg"}
                  title={t('environmental-and-circularity-index')}
                  description={
                    t("score-description-overall-index")
                  }
                  score={product.dpp.product_info.sac}
                />
                {/* <div className="sac-ses-new">
                  <ScoreInfo
                    pageKey={1}
                    icon={"/images/icon_sac_v2.svg"}
                    title={t('environmental-and-circularity')}
                    description={
                      t("score-description-enviromental-circularity")
                    }
                    score={product.dpp.product_info.sac}
                  />
                </div> */}
                {/* <div className="sac-ses-new">
                  <ScoreInfo
                    pageKey={1}
                    icon={"/images/icon_ses_v2.svg"}
                    title={"Economical and Social"}
                    description={
                      "Classification based on the economical and social performance of the value chain."
                    }
                    score={product.dpp.product_info.ses}
                  />
                </div> */}
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 1 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div className="product-rightside-main-div" ref={containerRef}>
                <div className="div-score-sac-ses-v2">
                  <ScoreInfo
                    pageKey={1}
                    icon={"/images/icon_AF.svg"}
                    title={t('environmental-and-circularity-index')}
                    description={
                      t("score-description-overall-index")
                    }
                    score={product.dpp.product_info.sac}
                  />
                  {/* <ScoreInfo
                pageKey={1}
                icon={"/images/icon_sac_v2.svg"}
                title={"Environmental and Circularity"}
                description={
                  "Classification based on the product's performance in environmental and circular aspects."
                }
                score={product.dpp.product_info.sac}
              />
              <ScoreInfo
                pageKey={1}
                icon={"/images/icon_ses_v2.svg"}
                title={"Economical and Social"}
                description={
                  "Classification based on the economical and social performance of the value chain."
                }
                score={product.dpp.product_info.ses}
              /> */}
                  {/* <div className="sac-ses-new-desktop">
                    <ScoreInfo
                      pageKey={1}
                      icon={"/images/icon_sac_v2.svg"}
                      title={t('environmental-and-circularity')}
                      description={
                        t("score-description-enviromental-circularity")
                      }
                      score={product.dpp.product_info.sac}
                    />
                  </div> */}
                  {/* <div className="sac-ses-new-desktop">
                    <ScoreInfo
                      pageKey={1}
                      icon={"/images/icon_ses_v2.svg"}
                      title={"Economical and Social"}
                      description={
                        "Classification based on the economical and social performance of the value chain."
                      }
                      score={product.dpp.product_info.ses}
                    />
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 2 && isMobile) {
        pageContent = (
          <div className="page3-container">
            <div className="div-journey-v2">
              <IndicatorInfo data={dataDetailedIndex} />
            </div>
          </div>
        );
      } else if (pageKey === 2 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div className="product-rightside-main-div" ref={containerRef}>
                <div className="div-journey-v2">
                  <IndicatorInfo data={dataDetailedIndex} />
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 3 && isMobile) {
        pageContent = (
          <div className="page4-container">
            <div className="product-maindiv-page4-v2">
              <React.Suspense fallback={null}>
                <GeoMap
                  journeyTotal_distance={
                    product.dpp.product_info.journey.total_distance
                  }
                  journeyTotal_countries={
                    product.dpp.product_info.journey.total_countries
                  }
                  journeyActivities={
                    product.dpp.product_info.journey.production_activities
                  }
                  isExpanded={isMapExpanded}
                  onToggleMap={handleToggleMap}
                  touchAreaRef={touchAreaRef}
                />
              </React.Suspense>
              {!isMapExpanded && (
                <div className="product-div-journey">
                    <Journey
                    steps={
                      product.dpp.product_info.journey.production_activities
                    }
                    onCompanyClick={handleCompanyClick}
                  />
                </div>
              )}
            </div>
          </div>
        );
      } else if (pageKey === 3 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div className="product-rightside-main-div" ref={containerRef}>
                <div className="product-maindiv-page4-v2 desktop">
              <React.Suspense fallback={null}>
                <GeoMap
                  journeyTotal_distance={
                    product.dpp.product_info.journey.total_distance
                  }
                  journeyTotal_countries={
                    product.dpp.product_info.journey.total_countries
                  }
                  journeyActivities={
                    product.dpp.product_info.journey.production_activities
                  }
                  isExpanded={isMapExpanded}
                  onToggleMap={handleToggleMap}
                  touchAreaRef={touchAreaRef}
                />
              </React.Suspense>
              {!isMapExpanded && (
                <div className="product-div-journey">
                      <Journey
                        steps={
                          product.dpp.product_info.journey.production_activities
                        }
                        onCompanyClick={handleCompanyClick}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 4 && isMobile) {
        pageContent = (
          <div className="page5-container">
            {/* <div className="product-maindiv-page5-v2-carousel">
          <AditionalInfo
            data={data}
            currentVisibleSlide={currentVisibleSlide}
            setCurrentVisibleSlide={setCurrentVisibleSlide}
            refNextBack={carouselRef}
          />
        </div> */}
            <ZaraTabs
              refNextBack={carouselRef}
              data={data}
              currentVisibleSlide={currentVisibleSlide}
              setCurrentVisibleSlide={setCurrentVisibleSlide}
              instanceId="mobile-additional"
              panelId="mobile-additional-panel"
            />
            <div id="mobile-additional-panel" role="tabpanel" aria-labelledby={`mobile-additional-tab-${currentVisibleSlide}`} className="product-maindiv-page5-v2" {...handlers}>
              {currentVisibleSlide === 0 && (
                <div className="properties" ref={scrollAreaRef}>
                  {<Properties categories={categoriesData} />}
                </div>
              )}
              {currentVisibleSlide === 1 && (
                <div className="care-instructions-products" ref={scrollAreaRef}>
                  <CareInstructions
                    care_instructions={product?.dpp?.product_info?.care_instructions}
                    productId={product?.id}
                  />
                </div>
              )}
              {currentVisibleSlide === 2 && (
                <div className="maintenance-discard" ref={scrollAreaRef}>
                  <MaintenanceDiscard
                    repair_text={getByCountryLang(
                      product?.dpp?.product_info?.maintenance_repair_instructions
                    )}
                    discard_text={getByCountryLang(
                      product?.dpp?.product_info?.discard_instructions
                    )}
                  />
                </div>
              )}
              {currentVisibleSlide === 3 && (
                <div className="bill-of-materials" ref={scrollAreaRef}>
                  {
                    <BillOfMaterials
                      data={product.dpp.product_info.bill_of_materials || []}
                    ></BillOfMaterials>
                  }
                </div>
              )}
            </div>
            {/* <div
          className="SimilarProducts"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMoveDebounced}
          onTouchEnd={handleTouchEnd}
          ref={touchAreaRef}
        >
          <SimilarProducts
            productList={product.dpp.product_info.similar_products}
            handleMainpage={handleMainpage}
            isOpenDesktop={isSimilarProductsOpen}
            setIsOpenDesktop={setSimilarProductsOpen}
          />
        </div> */}
          </div>
        );
      }
    } else {
      if (pageKey === 0 && isMobile) {
        pageContent = (
          <div className="page1-container-v2">
            <div className="header-content-v2">
              <button type="button" className="product-arrow-goback" aria-label={t("back")} onClick={handleBackClick}><img src="/images/icon_arrow_back.svg" alt="" aria-hidden="true" /></button>
              <div className="header-content-v2-content">
                <h1 style={{ margin: 0 }} className="header-content-v2-title">
                  {getNameByCountryLang(product?.dpp?.product_info?.name)}
                  {product.dpp.product_info.link && (
                    <button type="button" style={{ border: 0, padding: 0, background: "transparent" }} aria-label={t("open-product-link")} onClick={() => handleOpenLink(product.dpp.product_info.link)}><img className="link-produto" src="/images/icon_redirect.svg" alt="" aria-hidden="true" /></button>
                  )}
                </h1>
                <div className="header-content-v2-text">
                  {product.dpp.product_info.brand && (
                    <>{product.dpp.product_info.brand} · </>
                  )}
                  {t("made-in")}{" "}
                  {getCountryDescription(product.dpp.product_info.made_in)}
                </div>
              </div>
              {product.dpp.product_info.text_infos && (
                <button type="button" style={{ border: 0, padding: 0, background: "transparent" }} aria-label={t("more-product-information")} onClick={() => handleOpenTextPopup(product.dpp.product_info.text_infos)}><img className="textinfo-produto" src="/images/icon_knowmore.svg" alt="" aria-hidden="true" /></button>
              )}
            </div>
            <div className="product-maindiv-container-v2">
              <ProductGallery
                recycled={product.dpp.product_info.composition.recycled}
                compositionList={product.dpp.product_info.composition.fibers}
                certifications={product.dpp.product_info.certifications}
                productImgs={Array.isArray(product.dpp.product_info.img) ? product.dpp.product_info.img : []}
                thumbnailBlur={product.dpp.product_info.thumbnailBlur}
                productId={product.id || queryParameters.get("productId")}
                productLoadStartMs={productLoadStartRef.current}
                touchAreaRef={touchAreaRef}
                handleNavigation={returnToMainPage}
                product_info={product.dpp.product_info}
                score={product.dpp.product_info.sac}
              />
            </div>
            {shouldDisplayIndexTab ? (
              <div className="tabs-container">
                <div className="tabs-header" role="tablist" aria-label={t("product-information")} onKeyDown={handleTabKeyDown}>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-0"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 0}
                    tabIndex={activeTab === 0 ? 0 : -1}
                    className={`tab-button ${activeTab === 0 ? "active" : ""}`}
                    onClick={() => setActiveTab(0)}
                    >
                    {t("composition")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-1"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 1}
                    tabIndex={activeTab === 1 ? 0 : -1}
                    className={`tab-button ${activeTab === 1 ? "active" : ""}`}
                    onClick={() => setActiveTab(1)}
                    >
                    {t("index")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-2"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 2}
                    tabIndex={activeTab === 2 ? 0 : -1}
                    className={`tab-button ${activeTab === 2 ? "active" : ""}`}
                    onClick={() => setActiveTab(2)}
                    >
                    {t("certifications")}
                  </button>
                </div>
                <div
                  className="tabs-underline"
                  style={{ left: `${activeTab * 30.706}%` }}
                ></div>
                <div className="tabs-content" role="tabpanel" id="product-tabpanel" aria-labelledby={`product-tab-${activeTab}`} {...handlersTab}>
                  {activeTab === 0 && (
                    <div className="composition-new">
                      <div className="composition-new-circular">
                        <React.Suspense fallback={null}>
                          <CompositionGraphNew
                            recycled={
                              product.dpp.product_info.composition.recycled
                            }
                          />
                        </React.Suspense>
                      </div>
                      <div className="composition-new-bar">
                        <CompositionBarChart
                          compositions={
                            product.dpp.product_info.composition.fibers
                          }
                        />
                      </div>
                    </div>
                  )}
                  {activeTab === 1 && (
                    <div className="product-maindiv-score-v2">
                      <ScoreInfo
                        pageKey={0}
                        icon={"/images/icon_AF.svg"}
                        title={t("environmental-and-circularity-index")}
                        description={""}
                        score={product.dpp.product_info.sac}
                      />
                    </div>
                  )}
                  {activeTab === 2 && (
                    <div className="certifications-container-desktop-new">
                      <React.Suspense fallback={renderCertificationsTabFallback()}>
                        <CertificationsNew
                          certifications={product.dpp.product_info.certifications}
                        ></CertificationsNew>
                      </React.Suspense>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="tabs-container">
                <div className="tabs-header" role="tablist" aria-label={t("product-information")} onKeyDown={handleTabKeyDown}>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-1"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 1}
                    tabIndex={activeTab === 1 ? 0 : -1}
                    className={`tab-button ${activeTab === 1 ? "active" : ""}`}
                    onClick={() => setActiveTab(1)}
                    >
                    {t("composition")}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="product-tab-2"
                    aria-controls="product-tabpanel"
                    aria-selected={activeTab === 2}
                    tabIndex={activeTab === 2 ? 0 : -1}
                    className={`tab-button ${activeTab === 2 ? "active" : ""}`}
                    onClick={() => setActiveTab(2)}
                    >
                    {t("certifications")}
                  </button>
                </div>
                <div
                  className="tabs-underline without-score"
                  style={{ left: `${(activeTab - 1) * 46.06}%` }}
                ></div>
                <div className="tabs-content" role="tabpanel" id="product-tabpanel" aria-labelledby={`product-tab-${activeTab}`} {...handlersTab}>
                  {activeTab === 1 && (
                    <div className="composition-new">
                      <div className="composition-new-circular">
                        <React.Suspense fallback={null}>
                          <CompositionGraphNew
                            recycled={
                              product.dpp.product_info.composition.recycled
                            }
                          />
                        </React.Suspense>
                      </div>
                      <div className="composition-new-bar">
                        <CompositionBarChart
                          compositions={
                            product.dpp.product_info.composition.fibers
                          }
                        />
                      </div>
                    </div>
                  )}
                  {activeTab === 2 && (
                    <div className="certifications-container-desktop-new">
                      <React.Suspense fallback={renderCertificationsTabFallback()}>
                        <CertificationsNew
                          certifications={product.dpp.product_info.certifications}
                        ></CertificationsNew>
                      </React.Suspense>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      } else if (pageKey === 0 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div
                ref={containerRefTop} // Attach the ref
                className="product-rightside-main-div"
                style={showOverlayDesktop ? { overflow: "hidden" } : {}}
              >
                {showOverlayDesktop && <div className="overlay-desktop"></div>}
                <div className="product-maindiv-score-v2-desktop">
                  <div className="box-score-container-v2-desktop without-score">
                    {/* <ScoreInfo
                    pageKey={0}
                    icon={"/images/icon_AF.svg"}
                    title={t('environmental-and-circularity-index')}
                    description={""}
                    score={product.dpp.product_info.sac}
                  /> */}
                    <button
                      ref={exploreButtonRef}
                      autoFocus
                      type="button"
                      className="box-clicktosee"
                      aria-label={t("explore-dpp-cta")}
                      onClick={handleGoToPage}
                    >
                      <div className="box-clicktosee-text">
                        {" "}
                        {t('click-to-explore-our')}{" "}
                      </div>
                      <img
                        src={DppLogoDesktopProduct}
                        alt=""
                        aria-hidden="true"
                        className="box-clicktosee-img"
                      />
                      <img
                        src="/images/icon_arrow_back.svg"
                        alt=""
                        aria-hidden="true"
                        className="box-clicktosee-arrow"
                      />
                      {showOverlayDesktop && (
                        <img
                          src="/images/box-clicktosee-gif.gif"
                          alt="box-clicktosee-gif"
                          className="box-clicktosee-gif"
                        />
                      )}
                    </button>
                    {/* <div className="title-product-maindiv-score-v2-desktop">
                    <div
                      className="click-to-see-dpp-product"
                      onClick={handleGoToPage}
                    >
                      {" "}
                      Click to explore our{" "}
                    </div>
                    <img
                      src={DppLogoDesktopProduct}
                      alt="icon-arrows"
                      className="img-product-maindiv-score-v2-desktop"
                    />
                  </div> */}
                  </div>
                </div>
                <div className="certifications-container-desktop">
                  <React.Suspense fallback={renderCertificationsDesktopFallback()}>
                    <CertificationsDesktop
                      certifications={product.dpp.product_info.certifications}
                    ></CertificationsDesktop>
                  </React.Suspense>
                </div>
                {/* <div className="composition-desktop">
              <CompositionGraphDesktop
                compositionList={product.dpp.product_info.composition.fibers}
                recycled={product.dpp.product_info.composition.recycled}
              />
            </div> */}

                <div className="composition-new-container">
                  {!isMobile && (
                    <div className="certifications-desktop-title">
                      {" "}
                      {t('composition')}
                    </div>
                  )}
                  <div className="composition-new">
                    <div className="composition-new-circular">
                      <React.Suspense fallback={null}>
                        <CompositionGraphNew
                          recycled={product.dpp.product_info.composition.recycled}
                        />
                      </React.Suspense>
                    </div>
                    <div className="composition-new-bar">
                      <CompositionBarChart
                        compositions={
                          product.dpp.product_info.composition.fibers
                        }
                      />
                    </div>
                  </div>
                </div>
                <div className="additional-info-container-desktop">
                  <div className="additional-info-product-container">
                    <ZaraTabs
                      refNextBack={carouselRef}
                      data={data}
                      currentVisibleSlide={currentVisibleSlide}
                      setCurrentVisibleSlide={setCurrentVisibleSlide}
                      instanceId="desktop-journey-additional"
                      panelId="desktop-journey-additional-panel"
                    />
                  </div>
                  <div
                    role="tabpanel"
                    aria-labelledby={`desktop-journey-additional-tab-${currentVisibleSlide}`}
                    className="product-maindiv-page1-v2-desktop"
                    id="desktop-journey-additional-panel"
                    {...handlers}
                  >
                    <div className="container-arrows-content-desktop-product">
                      {currentVisibleSlide === 0 && (
                        <div className="properties">
                          {<Properties categories={categoriesData} />}
                        </div>
                      )}
                      {currentVisibleSlide === 1 && (
                        <div className="care-instructions-products">
                          <CareInstructions
                            care_instructions={product?.dpp?.product_info?.care_instructions}
                            productId={product?.id}
                          />
                        </div>
                      )}
                      {currentVisibleSlide === 2 && (
                        <div className="maintenance-discard">
                          <MaintenanceDiscard
                            repair_text={getByCountryLang(
                              product?.dpp?.product_info?.maintenance_repair_instructions
                            )}
                            discard_text={getByCountryLang(
                              product?.dpp?.product_info?.discard_instructions
                            )}
                          />
                        </div>
                      )}
                      {currentVisibleSlide === 3 && (
                        <div className="bill-of-materials">
                          {
                            <BillOfMaterials
                              data={product.dpp.product_info.bill_of_materials || []}
                            ></BillOfMaterials>
                          }
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 1 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div className="product-rightside-main-div" ref={containerRef}>
                <div className="div-score-sac-ses-v2">
                  <ScoreInfo
                    pageKey={1}
                    icon={"/images/icon_AF.svg"}
                    title={t('environmental-and-circularity-index')}
                    description={
                      t("score-description-overall-index")
                    }
                    score={product.dpp.product_info.sac}
                  />
                  {/* <ScoreInfo
                pageKey={1}
                icon={"/images/icon_sac_v2.svg"}
                title={"Environmental and Circularity"}
                description={
                  "Classification based on the product's performance in environmental and circular aspects."
                }
                score={product.dpp.product_info.sac}
              />
              <ScoreInfo
                pageKey={1}
                icon={"/images/icon_ses_v2.svg"}
                title={"Economical and Social"}
                description={
                  "Classification based on the economical and social performance of the value chain."
                }
                score={product.dpp.product_info.ses}
              /> */}
                  {/* <div className="sac-ses-new-desktop">
                    <ScoreInfo
                      pageKey={1}
                      icon={"/images/icon_sac_v2.svg"}
                      title={t('environmental-and-circularity')}
                      description={
                        t("score-description-enviromental-circularity")
                      }
                      score={product.dpp.product_info.sac}
                    />
                  </div> */}
                  {/* <div className="sac-ses-new-desktop">
                    <ScoreInfo
                      pageKey={1}
                      icon={"/images/icon_ses_v2.svg"}
                      title={"Economical and Social"}
                      description={
                        "Classification based on the economical and social performance of the value chain."
                      }
                      score={product.dpp.product_info.ses}
                    />
                  </div> */}
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 1 && isMobile) {
        pageContent = (
          <div className="page3-container">
            <div className="div-journey-v2">
              <IndicatorInfo data={dataDetailedIndex} />
            </div>
          </div>
        );
      } else if (pageKey === 2 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div className="product-rightside-main-div" ref={containerRef}>
                <div className="div-journey-v2">
                  <IndicatorInfo data={dataDetailedIndex} />
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 2 && isMobile) {
        pageContent = (
          <div className="page4-container">
            <div className="product-maindiv-page4-v2">
              <React.Suspense fallback={null}>
                <GeoMap
                  journeyTotal_distance={
                    product.dpp.product_info.journey.total_distance
                  }
                  journeyTotal_countries={
                    product.dpp.product_info.journey.total_countries
                  }
                  journeyActivities={
                    product.dpp.product_info.journey.production_activities
                  }
                  isExpanded={isMapExpanded}
                  onToggleMap={handleToggleMap}
                  touchAreaRef={touchAreaRef}
                />
              </React.Suspense>
              {!isMapExpanded && (
                <div className="product-div-journey">
                    <Journey
                    steps={
                      product.dpp.product_info.journey.production_activities
                    }
                    onCompanyClick={handleCompanyClick}
                  />
                </div>
              )}
            </div>
          </div>
        );
      } else if (pageKey === 3 && !isMobile) {
        pageContent = (
          <div className="page1-container-v2-desktop">
            <div className="product-maindiv-container-v2-desktop">
              <div className="product-rightside-main-div" ref={containerRef}>
                <div className="product-maindiv-page4-v2 desktop">
                  <React.Suspense fallback={null}>
                    <GeoMap
                      journeyTotal_distance={
                        product.dpp.product_info.journey.total_distance
                      }
                      journeyTotal_countries={
                        product.dpp.product_info.journey.total_countries
                      }
                      journeyActivities={
                        product.dpp.product_info.journey.production_activities
                      }
                      isExpanded={isMapExpanded}
                      onToggleMap={handleToggleMap}
                      touchAreaRef={touchAreaRef}
                    />
                  </React.Suspense>
                   {!isMapExpanded && (
                    <div className="product-div-journey">
                      <Journey
                        steps={
                          product.dpp.product_info.journey.production_activities
                        }
                        onCompanyClick={handleCompanyClick}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      } else if (pageKey === 3 && isMobile) {
        pageContent = (
          <div className="page5-container">
            {/* <div className="product-maindiv-page5-v2-carousel">
          <AditionalInfo
            data={data}
            currentVisibleSlide={currentVisibleSlide}
            setCurrentVisibleSlide={setCurrentVisibleSlide}
            refNextBack={carouselRef}
          />
        </div> */}
            <ZaraTabs
              refNextBack={carouselRef}
              data={data}
              currentVisibleSlide={currentVisibleSlide}
              setCurrentVisibleSlide={setCurrentVisibleSlide}
              instanceId="mobile-journey-additional"
              panelId="mobile-journey-additional-panel"
            />
            <div id="mobile-journey-additional-panel" role="tabpanel" aria-labelledby={`mobile-journey-additional-tab-${currentVisibleSlide}`} className="product-maindiv-page5-v2" {...handlers}>
              {currentVisibleSlide === 0 && (
                <div className="properties" ref={scrollAreaRef}>
                  {<Properties categories={categoriesData} />}
                </div>
              )}
              {currentVisibleSlide === 1 && (
                <div className="care-instructions-products" ref={scrollAreaRef}>
                  <CareInstructions
                    care_instructions={product?.dpp?.product_info?.care_instructions}
                    productId={product?.id}
                  />
                </div>
              )}
              {currentVisibleSlide === 2 && (
                <div className="maintenance-discard">
                  <MaintenanceDiscard
                    repair_text={getByCountryLang(
                      product?.dpp?.product_info?.maintenance_repair_instructions
                    )}
                    discard_text={getByCountryLang(
                      product?.dpp?.product_info?.discard_instructions
                    )}
                  />
                </div>
              )}
              {currentVisibleSlide === 3 && (
                <div className="bill-of-materials" ref={scrollAreaRef}>
                  {
                    <BillOfMaterials
                      data={product.dpp.product_info.bill_of_materials || []}
                    ></BillOfMaterials>
                  }
                </div>
              )}
            </div>
            {/* <div
          className="SimilarProducts"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMoveDebounced}
          onTouchEnd={handleTouchEnd}
          ref={touchAreaRef}
        >
          <SimilarProducts
            productList={product.dpp.product_info.similar_products}
            handleMainpage={handleMainpage}
            isOpenDesktop={isSimilarProductsOpen}
            setIsOpenDesktop={setSimilarProductsOpen}
          />
        </div> */}
          </div>
        );
      }
    }
  }

  useEffect(() => {
    if (product || isLoading) return;
    const redirectTimeout = setTimeout(() => navigate("/error", { replace: true }), 1000);
    return () => clearTimeout(redirectTimeout);
  }, [product, isLoading, navigate]);

  const isCurrentProduct = product && String(product.id) === String(requestedProductId);

  if (product && !isCurrentProduct) {
    return <div className="spinner" role="status">{t("loading-product")}</div>;
  }

  if (!product && !isLoading) {
    return <div role="alert">{productLoadError || t("product-load-error")}</div>;
  }

  if (isLoading) {
    console.log(`[Product] 🔄 Spinner VISÍVEL`);
    return <div className="spinner" role="status">{productLoadError || t("loading-product")}</div>;
  }
  console.log(`[Product] 📄 Página a renderizar — imagem ainda pode estar a carregar`);

  const mainContent = isMobile ? (
    <div className="product-fixed-page">
      <main>
      {showOverlay && (
        <div
          ref={overlayRef}
          className="overlay-product-main-page"
          role="dialog"
          aria-modal="true"
          aria-labelledby="product-tutorial-title"
        >
          <div className="overlay-product-main-page-content">
            <img
              alt="icon-overlay-product"
              className="icon-overlay-product"
              src="/images/gif_swipe.gif"
            />
            <div id="product-tutorial-title" className="overlay-product-text1">{t("swipe-up")}</div>
            <div className="overlay-product-text2">
              {t("to-explore-our")}
              <img
                alt="icon-logo-dpp-overlay"
                className="icon-logo-dpp-overlay"
                src={DppLogoDesktopProduct}
              />
            </div>
            <button
              ref={overlayCloseRef}
              type="button"
              className="overlay-product-button"
              onClick={handleDismissOverlay}
            >
              {t("got-it")}
            </button>
          </div>
        </div>
      )}
      <div className="product-container">
        <div className="page-indicator">
          {Array.from({ length: maxPages }, (_, index) => (
            <div
              key={index}
              className={`indicator-dot ${
                index === currentPage ? "active" : ""
              }`}
            ></div>
          ))}
        </div>
        {currentPage !== 0 && (
          <>
            <div className="header-content-v2">
              <button type="button" className="product-arrow-goback" aria-label={t("back")} onClick={handleBackClick}><img src="/images/icon_arrow_back.svg" alt="" aria-hidden="true" /></button>
              <div className="header-content-v2-content center">
                <h1 ref={pageHeadingRef} tabIndex={-1} style={{ margin: 0 }} className="header-content-v2-title center">
                  {pageTitle}
                </h1>
              </div>
            </div>
            <div className="header-v2">
              <ProductHeaderV2
                handleNavigation={returnToMainPage}
                product_info={product.dpp.product_info}
                score={product.dpp.product_info.sac}
                shouldDisplayIndexTab={shouldDisplayIndexTab}
              />
              {shouldDisplayIndexTab && (
                <ScoreVertical score={product.dpp.product_info.sac} />
              )}
            </div>
          </>
        )}
        <div
          className="swipe-main-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMoveDebounced}
          onTouchEnd={handleTouchEnd}
        >
          {pageContent}
        </div>
        <SurveyNudge
          isMobile={true}
          product={product}
          surveyUrl="https://research.typeform.com/to/ykTk4JdA"
        />
      </div>
      </main>
      <div className="navbar-footer">
        <Navbar MenuClick={handleOpenMenu} />
        <OverlayComponent isOpen={isMenuOpen} onClose={handleCloseMenu} />
      </div>
    </div>
  ) : (
    <div className="product-fixed-page">
      <div className="page-products-navbar"><NavbarDesktop /></div>
      <main className={`main-container-desktop ${currentPage === 0 ? "footer" : ""}`}>
      <div
        className={`gallery-container-desktop ${
          currentPage === 0 ? "footer" : ""
        }`}
      >
        <div className="header-container-v2-desktop">
          <button type="button" className="product-arrow-goback-desktop" aria-label={t("back")} onClick={handleBackClick}><img src="/images/icon_arrow_back.svg" alt="" aria-hidden="true" /></button>
          <div className="header-content-v2-content-desktop">
            <h1 ref={pageHeadingRef} tabIndex={-1} style={{ margin: 0 }} className="header-content-v2-title-desktop">
              {getNameByCountryLang(product?.dpp?.product_info?.name)}
              {product.dpp.product_info.link && (
                <button type="button" style={{ border: 0, padding: 0, background: "transparent" }} aria-label={t("open-product-link")} onClick={() => handleOpenLink(product.dpp.product_info.link)}>
                  <img className="link-produto-desktop" src="/images/icon_redirect.svg" alt="" aria-hidden="true" />
                </button>
              )}
              {product.dpp.product_info.text_infos && (
                <button type="button" style={{ border: 0, padding: 0, background: "transparent" }} aria-label={t("more-product-information")} onClick={() => handleOpenTextPopup(product.dpp.product_info.text_infos)}><img className="textinfo-produto-desktop" src="/images/icon_knowmore.svg" alt="" aria-hidden="true" /></button>
              )}
            </h1>
            <div className="header-content-v2-text-desktop">
              {product.dpp.product_info.brand}
            </div>
          </div>
          <div className="brand-container-v2-desktop">
            <div className="made-in-content-v2-text-desktop">
              {
                <div className="text-inline-desktop">
                  {t("made-in")}&nbsp;
                  <span style={{ display: "inline", marginLeft: "1px" }}>
                    {getCountryDescription(product.dpp.product_info.made_in)}
                  </span>{" "}
                </div>
              }
            </div>{" "}
            {/* <img
                alt="product-interrogation"
                className="product-interrogation"
                src="/images/icon_interrogation.svg"
              /> */}
          </div>
        </div>
        <ProductGallery
          recycled={product.dpp.product_info.composition.recycled}
          compositionList={product.dpp.product_info.composition.fibers}
          certifications={product.dpp.product_info.certifications}
          productImgs={Array.isArray(product.dpp.product_info.img) ? product.dpp.product_info.img : []}
          thumbnailBlur={product.dpp.product_info.thumbnailBlur}
          productId={product.id || queryParameters.get("productId")}
          productLoadStartMs={productLoadStartRef.current}
          touchAreaRef={touchAreaRef}
        />
      </div>
      <div
        className={`content-container-desktop ${
          currentPage === 0 ? "footer" : ""
        }`}
      >
        {currentPage !== 0 && (
          <div className="content-header-desktop">
            <button type="button" className="icon-arrow-back-desktop-pages" aria-label={t("back")} onClick={handleMainpage}><img src="/images/icon_arrow_back.svg" alt="" aria-hidden="true" /></button>
            <h2 style={{ margin: 0 }} className="header-desktop-content-text">{pageTitle}</h2>
          </div>
        )}
        {pageContent}
      </div>
      {currentPage !== 0 && (
        <div
          className={`scroll-bar-pages ${currentPage === 0 ? "footer" : ""}`}
        >
          {(shouldDisplayIndexTab ? [1, 2, 3] : [2, 3]).map(
            (pageNumber, index) => (
              <div
                key={index}
                className={`scroll-bar-pages-item ${
                  pageNumber === currentPage ? "active" : ""
                }`}
              />
            ),
          )}
          <div className="arrow-under-scrollbar-container">
            <button type="button" className={`arrow-under-scrollbar ${currentPage === (shouldDisplayIndexTab ? 1 : 2) ? "disabled" : ""}`} aria-label={t("previous-page")} disabled={currentPage === (shouldDisplayIndexTab ? 1 : 2)} onClick={() => handleArrowClick("up")}><img src="/images/arrow-under-scrollbar.svg" alt="" aria-hidden="true" /></button>
            <button type="button" className={`arrow-under-scrollbar down ${currentPage === 3 ? "disabled" : ""}`} aria-label={t("next-page")} disabled={currentPage === 3} onClick={() => handleArrowClick("down")}><img src="/images/arrow-under-scrollbar.svg" alt="" aria-hidden="true" /></button>
          </div>
        </div>
      )}
      {/* {currentPage === 3 && (
          <SimilarProducts
            productList={product.dpp.product_info.similar_products}
            handleMainpage={handleMainpage}
            isOpenDesktop={isSimilarProductsOpen}
            setIsOpenDesktop={setSimilarProductsOpen}
          />
        )} */}

      </main>
      {currentPage === 0 && (
        <div className="navbar-footer-product">
          <FooterDesktop />
        </div>
      )}
      <SurveyNudge
          isMobile={false}
          product={product}
          surveyUrl="https://research.typeform.com/to/ykTk4JdA"
        />
    </div>
  );

  return (
    <>
      {mainContent}
      {isCertificatesModalOpen && (
        <React.Suspense fallback={null}>
          <CompanyCertificatesPopup
            isOpen={isCertificatesModalOpen}
            onClose={handleCloseCertificatesModal}
            company={selectedCompany}
          />
        </React.Suspense>
      )}
      <TextInfo
        isOpen={isTextInfoModalOpen}
        onClose={handleCloseTextInfoModal}
        text={popupText}
      />
    </>
  );
};
export default Product;
